import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../lib/auth.jsx";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "viewer" });
  const [busy, setBusy] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data.users || []);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Acceso denegado");
    }
  };

  useEffect(() => { if (token) fetchUsers(); }, [token]);

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/admin/users`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Usuario creado");
      setForm({ email: "", name: "", password: "", role: "viewer" });
      setShowCreate(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const deleteUser = async (email) => {
    if (!confirm(`¿Eliminar usuario ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${email}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Usuario eliminado");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const inputCls = "w-full bg-[#f7f7f4] rounded-xl px-4 py-3 text-sm outline-none border border-[#e5e8e2] focus:border-[#133322]";
  const isMainAdmin = user?.role === "admin";

  return (
    <div className="eu-card overflow-hidden" data-testid="admin-users-tab">
      <div className="p-6 flex items-center justify-between border-b border-[#e5e8e2]">
        <div>
          <div className="eu-data-label">USUARIOS</div>
          <div className="font-display text-xl text-[#133322] mt-1">Gestión de usuarios ({users.length})</div>
        </div>
        {isMainAdmin && (
          <button onClick={() => setShowCreate(!showCreate)} className="eu-btn-primary text-sm py-2 px-4" data-testid="users-toggle-create">
            <i className={`fa-solid ${showCreate ? "fa-xmark" : "fa-plus"} text-xs`}></i>
            {showCreate ? "Cerrar" : "Nuevo usuario"}
          </button>
        )}
      </div>

      {showCreate && isMainAdmin && (
        <form onSubmit={createUser} className="p-6 grid sm:grid-cols-2 gap-4 border-b border-[#e5e8e2] bg-[#f7f7f4]/50" data-testid="users-create-form">
          <div>
            <label className="eu-data-label block mb-2">Nombre</label>
            <input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className={inputCls} data-testid="user-name" />
          </div>
          <div>
            <label className="eu-data-label block mb-2">Email*</label>
            <input required type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className={inputCls} data-testid="user-email" />
          </div>
          <div>
            <label className="eu-data-label block mb-2">Contraseña* (min 8)</label>
            <input required type="password" minLength={8} value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} className={inputCls} data-testid="user-password" />
          </div>
          <div>
            <label className="eu-data-label block mb-2">Rol</label>
            <select value={form.role} onChange={(e)=>setForm({...form, role:e.target.value})} className={inputCls} data-testid="user-role">
              <option value="viewer">Viewer (solo lectura)</option>
              <option value="admin">Admin (acceso total)</option>
            </select>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <button disabled={busy} className="eu-btn-primary text-sm" data-testid="user-submit">
              {busy ? "Creando..." : "Crear usuario"}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#f7f7f4] border-b border-[#e5e8e2]">
            <tr className="text-left">
              <th className="p-4 eu-data-label">Email</th>
              <th className="p-4 eu-data-label">Nombre</th>
              <th className="p-4 eu-data-label">Rol</th>
              <th className="p-4 eu-data-label">Creado</th>
              <th className="p-4 eu-data-label"></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan="5" className="p-12 text-center text-[#5c6b61]">Sin usuarios</td></tr>
            )}
            {users.map(u => (
              <tr key={u.email} className="border-b border-[#e5e8e2]" data-testid={`user-row-${u.email}`}>
                <td className="p-4 font-medium text-[#133322]">{u.email}</td>
                <td className="p-4">{u.name || "—"}</td>
                <td className="p-4">
                  <span className={`text-xs px-3 py-1 rounded-full ${u.role === "admin" ? "bg-[#133322] text-[#f7f7f4]" : "bg-[#9ead9b]/30 text-[#133322]"}`}>
                    {u.role}
                  </span>
                </td>
                <td className="p-4 text-xs text-[#5c6b61] font-mono-eu">{new Date(u.created_at).toLocaleDateString("es-ES")}</td>
                <td className="p-4 text-right">
                  {isMainAdmin && u.email !== user.email && (
                    <button onClick={() => deleteUser(u.email)} className="text-xs text-[#b45f3e] hover:underline" data-testid={`user-delete-${u.email}`}>
                      <i className="fa-solid fa-trash"></i> Eliminar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
